// Korean spelling-difficulty rules (learner-difficulties spec §2.2) —
// deterministic, explainable, no model. Mirrors the Korean half of
// lib/core/difficulty_rules.dart; both suites run the shared vectors in
// test/fixtures/difficulty_vectors.json, so the clients cannot drift.
//
// Kept apart from difficultyRules.ts (which also needs the Latin grading
// helpers) so the grader in types/studyQuestion.ts can use it for owner
// decision D1 without an import cycle.

import { decompose, decomposeSyllable } from './jamo';

/** One slip inside an answer: which class, which syllable of the stored
 *  form, the stored vs typed syllable(s) and the letters involved. */
export interface Slip {
  class: string;
  /** Syllable (Korean) or character (other scripts) index in the stored form. */
  syl: number;
  expected: string;
  got: string;
  /** The letters (ㅐ / ㅔ, "die" / "der") — what a teacher may see. */
  exp_part: string;
  got_part: string;
}

export interface TypedAnswerCheck {
  /** Give-up, empty, or failed the closeness gate: a different word, not a
   *  spelling slip. No exposure, no slips. */
  far: boolean;
  /** Classes the stored form gives a chance to get wrong (∪ slipped). */
  exposed: string[];
  /** Classes the learner got wrong in THIS answer (⊆ exposed). */
  slipped: string[];
  slips: Slip[];
}

export const FAR: TypedAnswerCheck = { far: true, exposed: [], slipped: [], slips: [] };

// ---- class tables -------------------------------------------------------

const VOWEL_FAMILIES: Array<[string, string[]]> = [
  ['ko.v.ae_e', ['ㅐ', 'ㅔ']],
  ['ko.v.yae_ye', ['ㅒ', 'ㅖ']],
  ['ko.v.wae_oe_we', ['ㅙ', 'ㅚ', 'ㅞ']],
  ['ko.v.eo_o', ['ㅓ', 'ㅗ']],
  ['ko.v.yeo_yo', ['ㅕ', 'ㅛ']],
  ['ko.v.eu_u', ['ㅡ', 'ㅜ']],
];
/** ㅢ is said like ㅣ, ㅡ or (as "of") ㅔ; the spelling keeps ㅢ. Exposed
 *  only by a stored ㅢ — the letter that has to be produced. */
const UI_PARTNERS = new Set(['ㅣ', 'ㅡ', 'ㅔ']);

const INITIAL_FAMILIES: Array<[string, string[]]> = [
  ['ko.c.g_k_kk', ['ㄱ', 'ㅋ', 'ㄲ']],
  ['ko.c.d_t_tt', ['ㄷ', 'ㅌ', 'ㄸ']],
  ['ko.c.b_p_pp', ['ㅂ', 'ㅍ', 'ㅃ']],
  ['ko.c.j_ch_jj', ['ㅈ', 'ㅊ', 'ㅉ']],
  ['ko.c.s_ss', ['ㅅ', 'ㅆ']],
];

const BATCHIM_GROUPS: Array<[string, string[]]> = [
  ['ko.b.t', ['ㄷ', 'ㅅ', 'ㅆ', 'ㅈ', 'ㅊ', 'ㅌ', 'ㅎ']],
  ['ko.b.k', ['ㄱ', 'ㅋ', 'ㄲ']],
  ['ko.b.p', ['ㅂ', 'ㅍ']],
];

/** Double batchim → its two written letters. */
const DOUBLE_TAILS: Record<string, [string, string]> = {
  ㄳ: ['ㄱ', 'ㅅ'], ㄵ: ['ㄴ', 'ㅈ'], ㄶ: ['ㄴ', 'ㅎ'], ㄺ: ['ㄹ', 'ㄱ'], ㄻ: ['ㄹ', 'ㅁ'],
  ㄼ: ['ㄹ', 'ㅂ'], ㄽ: ['ㄹ', 'ㅅ'], ㄾ: ['ㄹ', 'ㅌ'], ㄿ: ['ㄹ', 'ㅍ'], ㅀ: ['ㄹ', 'ㅎ'], ㅄ: ['ㅂ', 'ㅅ'],
};

export const KO_OTHER = 'ko.other';

/** Every Korean class the rules can produce, in the server's sort order. */
export const KOREAN_CLASSES: readonly string[] = [
  ...VOWEL_FAMILIES.map(([k]) => k), 'ko.v.ui',
  ...INITIAL_FAMILIES.map(([k]) => k),
  ...BATCHIM_GROUPS.map(([k]) => k), 'ko.b.double',
  'ko.s.liaison', 'ko.s.h_drop', 'ko.s.aspiration', 'ko.s.palatal', 'ko.s.nasal', 'ko.s.liquid', 'ko.s.tense',
  KO_OTHER,
];

const familyOf = (table: Array<[string, string[]]>, j: string): string | undefined =>
  table.find(([, m]) => m.includes(j))?.[0];

function vowelClass(exp: string, got: string): string | undefined {
  if ((exp === 'ㅢ' && UI_PARTNERS.has(got)) || (got === 'ㅢ' && UI_PARTNERS.has(exp))) return 'ko.v.ui';
  const f = familyOf(VOWEL_FAMILIES, exp);
  return f && f === familyOf(VOWEL_FAMILIES, got) ? f : undefined;
}

function initialClass(exp: string, got: string): string | undefined {
  const f = familyOf(INITIAL_FAMILIES, exp);
  return f && f === familyOf(INITIAL_FAMILIES, got) ? f : undefined;
}

function tailClass(exp: string, got: string): string | undefined {
  if (exp && got) {
    const f = familyOf(BATCHIM_GROUPS, exp);
    if (f && f === familyOf(BATCHIM_GROUPS, got)) return f;
  }
  if (DOUBLE_TAILS[exp]?.includes(got) || DOUBLE_TAILS[got]?.includes(exp)) return 'ko.b.double';
  return undefined;
}

// ---- pronounced form (sound-change rules, §2.2) -------------------------

const LEADS = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';
const VOWELS = 'ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ';
const TAILS = ['', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ',
  'ㄽ', 'ㄾ', 'ㄿ', 'ㅀ', 'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];

export function composeSyllable(lead: string, vowel: string, tail: string): string {
  const l = LEADS.indexOf(lead);
  const v = VOWELS.indexOf(vowel);
  const t = TAILS.indexOf(tail);
  if (l < 0 || v < 0 || t < 0) return '';
  return String.fromCodePoint(0xac00 + (l * 21 + v) * 28 + t);
}

/** The sound a final consonant has before a consonant: k, t or p (as ㄱ/ㄷ/ㅂ). */
function neutral(tail: string): string | undefined {
  if ('ㄱㄲㅋㄳㄺ'.includes(tail)) return 'ㄱ';
  if ('ㄷㅅㅆㅈㅊㅌㅎ'.includes(tail)) return 'ㄷ';
  if ('ㅂㅍㅄㄿ'.includes(tail)) return 'ㅂ';
  return undefined;
}

const ASPIRATE: Record<string, string> = { ㄱ: 'ㅋ', ㄷ: 'ㅌ', ㅈ: 'ㅊ', ㅂ: 'ㅍ' };
const TENSE: Record<string, string> = { ㄱ: 'ㄲ', ㄷ: 'ㄸ', ㅂ: 'ㅃ', ㅅ: 'ㅆ', ㅈ: 'ㅉ' };

export interface SoundChange {
  key: string;
  /** The two syllables as they are said. */
  said: [string, string];
}

/**
 * The sound change at the boundary between two written syllables, or null.
 * One rule per boundary; the specific rules (ㅎ, palatalisation) are tried
 * before the general liaison so 좋아 is [조아], not [조하].
 */
export function soundChangeAt(first: string, second: string): SoundChange | null {
  const a = decomposeSyllable(first);
  const b = decomposeSyllable(second);
  if (!a || !b) return null;
  const [la, va, ta] = a;
  const [lb, vb, tb] = b;
  if (!ta) return null;
  const out = (key: string, ta2: string, lb2: string): SoundChange => ({
    key,
    said: [composeSyllable(la, va, ta2), composeSyllable(lb2, vb, tb)],
  });
  const hRest: Record<string, string> = { ㅎ: '', ㄶ: 'ㄴ', ㅀ: 'ㄹ' };

  // ㅎ before a vowel is dropped (좋아 [조아], 많이 [마니]).
  if (ta in hRest && lb === 'ㅇ') {
    const rest = hRest[ta]!;
    return rest ? out('ko.s.h_drop', '', rest) : out('ko.s.h_drop', '', 'ㅇ');
  }
  // ㅎ next to ㄱ ㄷ ㅈ (ㅂ) makes it aspirated (좋다 [조타], 축하 [추카]).
  if (ta in hRest && ASPIRATE[lb] && lb !== 'ㅂ') return out('ko.s.aspiration', hRest[ta]!, ASPIRATE[lb]!);
  if (lb === 'ㅎ') {
    const keep: Record<string, string> = { ㄺ: 'ㄹ', ㄼ: 'ㄹ', ㄵ: 'ㄴ' };
    const moved = DOUBLE_TAILS[ta]?.[1] ?? ta;
    const n = moved === 'ㅈ' || moved === 'ㅊ' ? 'ㅈ' : neutral(moved);
    if (n && ASPIRATE[n]) return out('ko.s.aspiration', keep[ta] ?? '', ASPIRATE[n]!);
  }
  // ㄷ/ㅌ + 이 → 지/치 (같이 [가치], 굳이 [구지]).
  if (lb === 'ㅇ' && vb === 'ㅣ' && (ta === 'ㄷ' || ta === 'ㅌ' || ta === 'ㄾ')) {
    if (ta === 'ㄾ') return out('ko.s.palatal', 'ㄹ', 'ㅊ');
    return out('ko.s.palatal', '', ta === 'ㄷ' ? 'ㅈ' : 'ㅊ');
  }
  // A final consonant moves over to a following vowel (옷이 [오시], 읽어 [일거]).
  if (lb === 'ㅇ' && ta !== 'ㅇ') {
    const dbl = DOUBLE_TAILS[ta];
    return dbl ? out('ko.s.liaison', dbl[0], dbl[1]) : out('ko.s.liaison', '', ta);
  }
  // [k t p] before ㄴ/ㅁ → ㅇ ㄴ ㅁ (국물 [궁물], 십만 [심만]).
  const n = neutral(ta);
  if (n && (lb === 'ㄴ' || lb === 'ㅁ')) {
    return out('ko.s.nasal', { ㄱ: 'ㅇ', ㄷ: 'ㄴ', ㅂ: 'ㅁ' }[n]!, lb);
  }
  // ㄴ next to ㄹ → ㄹㄹ (신라 [실라], 설날 [설랄]).
  if (ta === 'ㄴ' && lb === 'ㄹ') return out('ko.s.liquid', 'ㄹ', 'ㄹ');
  if ((ta === 'ㄹ' || ta === 'ㅀ' || ta === 'ㄾ') && lb === 'ㄴ') return out('ko.s.liquid', ta === 'ㄹ' ? 'ㄹ' : 'ㄹ', 'ㄹ');
  // A plain consonant after [k t p] is said tense (학교 [학꾜], 식당 [식땅]).
  if (n && TENSE[lb]) return out('ko.s.tense', ta, TENSE[lb]!);
  return null;
}

// ---- alignment + closeness ---------------------------------------------

const HANGUL = /[가-힣]/u;
export const hasHangul = (s: string): boolean => HANGUL.test(s);

const foldKo = (s: string): string => s.normalize('NFC').trim().replace(/\s+/g, ' ');

function subCost(a: string, b: string): number {
  if (a === b) return 0;
  const x = decomposeSyllable(a);
  const y = decomposeSyllable(b);
  if (!x || !y) return 3;
  return (x[0] !== y[0] ? 1 : 0) + (x[1] !== y[1] ? 1 : 0) + (x[2] !== y[2] ? 1 : 0);
}

/** Needleman–Wunsch over syllables: substitution = differing jamo (0–3),
 *  insertion/deletion = 3. Pairs of [expectedIndex | -1, typedIndex | -1]. */
export function alignSyllables(e: string[], t: string[]): Array<[number, number]> {
  const m = e.length;
  const n = t.length;
  const d: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j * 3 : j === 0 ? i * 3 : 0)));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      d[i]![j] = Math.min(
        d[i - 1]![j - 1]! + subCost(e[i - 1]!, t[j - 1]!),
        d[i - 1]![j]! + 3,
        d[i]![j - 1]! + 3,
      );
    }
  }
  const out: Array<[number, number]> = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && d[i]![j] === d[i - 1]![j - 1]! + subCost(e[i - 1]!, t[j - 1]!)) {
      out.push([i - 1, j - 1]);
      i--; j--;
    } else if (i > 0 && d[i]![j] === d[i - 1]![j]! + 3) {
      out.push([i - 1, -1]);
      i--;
    } else {
      out.push([-1, j - 1]);
      j--;
    }
  }
  return out.reverse();
}

/** Optimal-string-alignment distance (restricted Damerau–Levenshtein). */
export function osaDistance(a: readonly string[], b: readonly string[]): number {
  const m = a.length;
  const n = b.length;
  const d: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, d[i - 2]![j - 2]! + 1);
      d[i]![j] = v;
    }
  }
  return d[m]![n]!;
}

/** Closeness gate: at most max(2, ⌈units / 4⌉) edits from the stored form. */
export const closeEnough = (distance: number, units: number): boolean =>
  distance <= Math.max(2, Math.ceil(units / 4));

// ---- exposure + classification -----------------------------------------

/** The Korean classes a stored form exposes (opportunities). Once per word. */
export function koreanExposure(expected: string): string[] {
  const syl = [...foldKo(expected)];
  const out = new Set<string>();
  for (let i = 0; i < syl.length; i++) {
    const p = decomposeSyllable(syl[i]!);
    if (!p) continue;
    const [l, v, tl] = p;
    const vf = familyOf(VOWEL_FAMILIES, v);
    if (vf) out.add(vf);
    if (v === 'ㅢ') out.add('ko.v.ui');
    const cf = familyOf(INITIAL_FAMILIES, l);
    if (cf) out.add(cf);
    if (tl) {
      const bf = familyOf(BATCHIM_GROUPS, tl);
      if (bf) out.add(bf);
      if (DOUBLE_TAILS[tl]) out.add('ko.b.double');
    }
    if (i + 1 < syl.length) {
      const sc = soundChangeAt(syl[i]!, syl[i + 1]!);
      if (sc) out.add(sc.key);
    }
  }
  return KOREAN_CLASSES.filter((k) => out.has(k));
}

/** Classify a typed Korean answer against one stored form (§2.2). */
export function checkKorean(expected: string, typed: string | null | undefined): TypedAnswerCheck {
  const e = foldKo(expected);
  const t = foldKo(typed ?? '');
  if (t === '') return FAR;
  const ej = decompose(e);
  if (!closeEnough(osaDistance(ej, decompose(t)), ej.length)) return FAR;

  const es = [...e];
  const ts = [...t];
  const pairs = alignSyllables(es, ts);
  const typedAt = new Map<number, number>();
  for (const [ei, ti] of pairs) if (ei >= 0 && ti >= 0) typedAt.set(ei, ti);

  const slips: Slip[] = [];
  const consumed = new Set<number>();
  // a. Written as it sounds: the typed pair equals the said pair.
  for (let k = 0; k + 1 < es.length; k++) {
    const sc = soundChangeAt(es[k]!, es[k + 1]!);
    const t0 = typedAt.get(k);
    const t1 = typedAt.get(k + 1);
    if (!sc || t0 === undefined || t1 === undefined || t1 !== t0 + 1) continue;
    const got = ts[t0]! + ts[t1]!;
    const want = es[k]! + es[k + 1]!;
    if (got !== want && got === sc.said.join('')) {
      slips.push({ class: sc.key, syl: k, expected: want, got, exp_part: want, got_part: got });
      consumed.add(k);
      consumed.add(k + 1);
    }
  }
  // b–e. Letter by letter in each substituted syllable.
  for (const [ei, ti] of pairs) {
    if (ei < 0 || ti < 0 || consumed.has(ei)) continue;
    const want = es[ei]!;
    const got = ts[ti]!;
    if (want === got) continue;
    const x = decomposeSyllable(want);
    const y = decomposeSyllable(got);
    if (!x || !y) {
      slips.push({ class: KO_OTHER, syl: ei, expected: want, got, exp_part: want, got_part: got });
      continue;
    }
    if (x[2] !== y[2]) {
      slips.push({ class: tailClass(x[2], y[2]) ?? KO_OTHER, syl: ei, expected: want, got, exp_part: x[2], got_part: y[2] });
    }
    if (x[1] !== y[1]) {
      slips.push({ class: vowelClass(x[1], y[1]) ?? KO_OTHER, syl: ei, expected: want, got, exp_part: x[1], got_part: y[1] });
    }
    if (x[0] !== y[0]) {
      slips.push({ class: initialClass(x[0], y[0]) ?? KO_OTHER, syl: ei, expected: want, got, exp_part: x[0], got_part: y[0] });
    }
  }
  slips.sort((a, b) => a.syl - b.syl);
  const slipped = [...new Set(slips.map((s) => s.class))];
  const exposedSet = new Set([...koreanExposure(e), ...slipped]);
  const exposed = KOREAN_CLASSES.filter((k) => exposedSet.has(k));
  return { far: false, exposed, slipped: KOREAN_CLASSES.filter((k) => slipped.includes(k)), slips: slips.slice(0, 10) };
}

/** Owner decision D1: the answer's only difference is a slip inside a known
 *  Korean confusion family (ㅐ/ㅔ, batchim, written-as-said …) — not ko.other. */
export function isKoreanFamilySlip(expected: string, typed: string): boolean {
  if (!hasHangul(expected)) return false;
  const c = checkKorean(expected, typed);
  return !c.far && c.slips.length > 0 && c.slips.every((s) => s.class !== KO_OTHER);
}
